import * as React from 'react'
import { useLocale } from 'next-intl'
import { cn } from '@segway/core'

const MounthChangeIcon = (
  <svg width="22" height="40" viewBox="0 0 22 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M12.0607 16.0607L11 15L6 20L11 25L12.0607 23.9393L8.12132 20L12.0607 16.0607Z"
      fill="#BBBBBD"
    />
  </svg>
)

const YearChangeIcon = (
  <svg width="22" height="40" viewBox="0 0 22 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M12.0607 16.0607L11 15L6 20L11 25L12.0607 23.9393L8.12132 20L12.0607 16.0607Z"
      fill="#BBBBBD"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M18.0607 16.0607L17 15L12 20L17 25L18.0607 23.9393L14.1213 20L18.0607 16.0607Z"
      fill="#BBBBBD"
    />
  </svg>
)

export interface DatePickerCalendarProps {
  value?: string
  handleCalendarChange: (value: string) => void
  className?: string
}

export const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({
  value,
  handleCalendarChange,
  className,
}) => {
  const locale = useLocale()
  const [selectedDate, setSelectedDate] = React.useState(() => {
    if (!value) return new Date('1990-01-01')
    const result = new Date(value)
    return result.toString() === 'Invalid Date' ? new Date('1990-01-01') : new Date(value)
  })
  const [viewDate, setViewDate] = React.useState(selectedDate)

  const DAYS = React.useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2021, 7, 1 + i)),
      ).map((item) => item + '.'),
    [locale],
  )

  const MONTHS = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2000, i, 1)),
      ),
    [locale],
  )

  const days = React.useMemo<{ isCurrentMouth: boolean; value: number }[]>(() => {
    const daysArray = []
    const totalDays = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate()
    const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay()
    const lastDayPrevMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 0).getDate()
    const totalSlots = 42

    // 填充上个月的日期
    for (let i = firstDay - 1; i >= 0; i--) {
      daysArray.push({
        isCurrentMouth: false,
        value: new Date(
          viewDate.getFullYear(),
          viewDate.getMonth() - 1,
          lastDayPrevMonth - i,
        ).getTime(),
      })
    }

    // 填充当前月的日期
    for (let i = 1; i <= totalDays; i++) {
      daysArray.push({
        isCurrentMouth: true,
        value: new Date(viewDate.getFullYear(), viewDate.getMonth(), i).getTime(),
      })
    }

    // 填充下个月的日期
    const remainingSlots = totalSlots - daysArray.length
    for (let i = 1; i <= remainingSlots; i++) {
      daysArray.push({
        isCurrentMouth: false,
        value: new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, i).getTime(),
      })
    }

    return daysArray
  }, [viewDate])

  const handleDayClick = React.useCallback(
    (dayNumber: number) => {
      const newDate = new Date(dayNumber)
      setSelectedDate(newDate)

      const year = newDate.getFullYear()
      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const day = String(newDate.getDate()).padStart(2, '0')
      handleCalendarChange(`${day}/${month}/${year}`)
    },
    [handleCalendarChange],
  )

  const handleMouthChange = React.useCallback(
    (direction: 'prev' | 'next') => {
      setViewDate(
        new Date(viewDate.getFullYear(), viewDate.getMonth() + (direction === 'prev' ? -1 : 1), 1),
      )
    },
    [viewDate],
  )

  const handleYearChange = React.useCallback(
    (direction: 'prev' | 'next') => {
      setViewDate(
        new Date(viewDate.getFullYear() + (direction === 'prev' ? -1 : 1), viewDate.getMonth(), 1),
      )
    },
    [viewDate],
  )

  return (
    <div
      className={cn(
        className,
        'z-10 w-full max-w-[348px] rounded-[8px] bg-white p-[12px] shadow-xl',
      )}
      onClick={(e) => {
        e.preventDefault()
      }}>
      <div className="mb-2 flex items-center justify-between border-b-[1px]">
        <div>
          <button onClick={() => handleYearChange('prev')}>{YearChangeIcon}</button>
          <button onClick={() => handleMouthChange('prev')}>{MounthChangeIcon}</button>
        </div>

        <span className="capitalize">
          {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
        </span>

        <div>
          <button className="rotate-180" onClick={() => handleMouthChange('next')}>
            {MounthChangeIcon}
          </button>
          <button className="rotate-180" onClick={() => handleYearChange('next')}>
            {YearChangeIcon}
          </button>
        </div>
      </div>

      <div className="px-[12px] py-[8px]">
        <div className="grid grid-cols-7 place-items-center gap-1 text-center text-[14px]">
          {DAYS.map((d) => (
            <div key={d} className="px-[6px] py-[3px]">
              <span>{d}</span>
            </div>
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 place-items-center gap-1 text-center">
          {days.map(({ isCurrentMouth, value }, i) => {
            const date = new Date(value)
            const isSelected =
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear()
            return (
              <div className="px-[6px] py-[3px]" key={i}>
                <button
                  onClick={() => handleDayClick(value)}
                  className={cn(
                    'h-[24px] w-[24px] rounded text-[14px] hover:bg-[#F3F3F4]',
                    isSelected && 'bg-[#DA291C] text-white hover:bg-[#DA291C]',
                    !isCurrentMouth && 'text-[#E1E1E4]',
                    'capitalize',
                  )}>
                  <span>{date.getDate()}</span>
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
